import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import HeroIcon from "../../components/common/HeroIcon";
import SortableTh from "../../components/common/SortableTh";
import ModalRoute from "../../components/common/ModalRoute";
import FitScoreRadial from "../../components/audience/FitScoreRadial";
import PatternDetail from "../pattern";
import {
  formatNumber,
  formatRatioCompact,
  getCreatorImpactLabel,
  getReachLabel,
  LABEL_TINT_CLASSES,
  formatAnalysisTooltip,
} from "../../components/patterns/utils";
import { audiencesService } from "../../services/audiences";
import { patternsService } from "../../services/patterns";
import { PATTERN_TYPE_DESCRIPTIONS } from "../../constants/patternTypes";

const EMPTY_SENTINEL = Infinity;

const SORT_OPTIONS = [
  { id: "rank", label: "Rank" },
  { id: "brand_fit", label: "Brand fit", requiresBrandFit: true },
  { id: "reach", label: "Reach" },
  { id: "creator_impact", label: "Creator impact" },
  { id: "pattern", label: "Pattern name" },
  { id: "type", label: "Type" },
];

const Highlights = ({ audience }) => {
  const navigate = useNavigate();
  const { patternType: routePatternType, patternId: routePatternId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentBrand } = useSelector((state) => state.brand);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [sortBy, setSortBy] = useState("rank");
  const [sortDir, setSortDir] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef(null);

  const brandHash = currentBrand?.hash || null;
  const analysisIdParam = (searchParams.get("analysis_id") || "").trim();

  const patternDetailBaseUrl =
    audience?.hash && routePatternType && routePatternId
      ? `/audience/${audience.hash}/highlights/${encodeURIComponent(routePatternType)}/${encodeURIComponent(routePatternId)}/`
      : null;

  const patternDetailOnClose = () => navigate(`/audience/${audience?.hash}/highlights/`);

  useEffect(() => {
    patternsService.getPatternCategories(
      (response) => setCategories(response.data?.categories || []),
      () => setCategories([])
    );
  }, []);

  useEffect(() => {
    if (!showFilters) return;
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilters]);

  const loadHighlights = useCallback(() => {
    if (!audience?.hash) return;
    setLoading(true);
    setError(null);
    audiencesService.getHighlights(
      audience.hash,
      brandHash ? { brand_hash: brandHash } : {},
      (response) => {
        setItems(response.data?.patterns || []);
        setLoading(false);
      },
      () => {
        setError("Could not load highlights.");
        setLoading(false);
      }
    );
  }, [audience?.hash, brandHash]);

  useEffect(() => {
    loadHighlights();
  }, [loadHighlights]);

  const handleGenerate = (force = false) => {
    if (!audience?.hash || generating) return;
    setGenerating(true);
    setError(null);
    const params = brandHash ? { brand_hash: brandHash } : {};
    if (force) {
      params.force = true;
    }
    audiencesService.generateHighlights(
      audience.hash,
      params,
      (response) => {
        setItems(response.data?.patterns || []);
        setGenerating(false);
      },
      () => {
        setError(force ? "Could not generate highlights." : "Could not refresh highlights.");
        setGenerating(false);
      }
    );
  };

  const patternTypes = useMemo(
    () => categories.flatMap((category) => category.pattern_types || []),
    [categories]
  );
  const showBrandFitColumn = useMemo(
    () => items.some((item) => item.score_brand_fit != null),
    [items]
  );
  const sortOptions = useMemo(
    () => SORT_OPTIONS.filter((option) => !option.requiresBrandFit || showBrandFitColumn),
    [showBrandFitColumn]
  );

  useEffect(() => {
    if (sortBy === "brand_fit" && !showBrandFitColumn) {
      setSortBy("rank");
    }
  }, [sortBy, showBrandFitColumn]);

  const filteredItems = useMemo(() => {
    if (!selectedTypes.length) return items;
    return items.filter((item) => selectedTypes.includes(item.pattern_type));
  }, [items, selectedTypes]);

  const sortedItems = useMemo(
    () => sortHighlightItems(filteredItems, sortBy, sortDir, patternTypes),
    [filteredItems, sortBy, sortDir, patternTypes]
  );

  const availableTypes = patternTypes.filter((type) =>
    items.some((item) => item.pattern_type === type.id)
  );
  const activeFilterCount = selectedTypes.length + (sortBy !== "rank" ? 1 : 0);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir(key === "pattern" || key === "type" ? "asc" : "desc");
    }
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSortBy("rank");
    setSortDir("desc");
  };

  const sortThProps = {
    sortBy,
    sortDirection: sortDir,
    onSort: handleSort,
    activeClassName: "patterns-sort-th-active",
    showSortArrow: false,
  };

  const selectedTypeMeta = patternTypes.find((type) => type.id === routePatternType);
  const highlightItem = items.find(
    (item) =>
      item.pattern_key === routePatternId &&
      item.pattern_type === routePatternType &&
      (!analysisIdParam || String(item.analysis_id) === analysisIdParam)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <OrbitalLoader size={96} />
      </div>
    );
  }

  if (error && !items.length) {
    return <div className="text-theme-text opacity-70">{error}</div>;
  }

  if (!items.length) {
    return (
      <div className="rounded-lg border border-theme-secondary bg-theme-secondary/20 p-8 text-center">
        <p className="text-theme-text opacity-70">No highlights yet</p>
        <button
          type="button"
          onClick={() => handleGenerate(true)}
          disabled={generating}
          className="mt-4 px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-semibold hover:opacity-85 disabled:opacity-50"
        >
          {generating ? "Generating..." : "Generate highlights"}
        </button>
        {error && <p className="mt-3 text-sm text-theme-text opacity-70">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="flex items-baseline gap-2 text-xl font-semibold text-theme-text">
          Top Pattern Highlights
          <span className="text-base font-normal opacity-50">({sortedItems.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleGenerate(false)}
            disabled={generating}
            className="rounded-md border border-theme-secondary bg-theme-main p-2 text-theme-text opacity-60 transition-opacity hover:opacity-100 disabled:opacity-30"
            title="Refresh top patterns"
            aria-label="Refresh top patterns"
          >
            <svg className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <div ref={filtersRef} className="relative">
            <button
              type="button"
              onClick={() => setShowFilters((value) => !value)}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                activeFilterCount > 0
                  ? "border-theme-primary bg-theme-primary/10 text-theme-primary"
                  : "border-theme-secondary bg-theme-main text-theme-text hover:opacity-80"
              }`}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-theme-primary px-1.5 py-0.5 text-xs font-semibold text-theme-button">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {showFilters && (
              <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-lg border border-theme-secondary bg-theme-secondary p-4 shadow-lg">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-theme-text">Filters</span>
                  {activeFilterCount > 0 && (
                    <button type="button" onClick={clearFilters} className="text-xs text-theme-primary hover:underline">
                      Clear all
                    </button>
                  )}
                </div>
                <div>
                  <label htmlFor="highlights-sort-by" className="mb-1.5 block text-xs text-theme-text opacity-60">
                    Sort by
                  </label>
                  <select
                    id="highlights-sort-by"
                    value={sortBy}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSortBy(next);
                      setSortDir(next === "pattern" || next === "type" ? "asc" : "desc");
                    }}
                    className="w-full rounded-md border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-4 border-t border-theme-secondary pt-4">
                  <span className="mb-1.5 block text-xs text-theme-text opacity-60">Pattern type</span>
                  <div className="max-h-48 space-y-1 overflow-y-auto">
                    {availableTypes.map((type) => {
                      const selected = selectedTypes.includes(type.id);
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() =>
                            setSelectedTypes((current) =>
                              selected ? current.filter((id) => id !== type.id) : [...current, type.id]
                            )
                          }
                          className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
                            selected
                              ? "bg-theme-primary/10 text-theme-primary"
                              : "text-theme-text hover:bg-theme-main"
                          }`}
                        >
                          <HeroIcon name={type.icon || "squares-2x2"} className="h-4 w-4 shrink-0" />
                          <span className="flex-1">{type.label_long || type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <p className="mb-3 text-sm text-theme-text opacity-70">{error}</p>}

      <div className="overflow-hidden rounded-xl border border-theme-secondary bg-theme-main shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed divide-y divide-theme-secondary">
            <colgroup>
              <col style={{ width: "2.25rem" }} />
              <col />
              <col style={{ width: "13rem" }} />
              <col style={{ width: "10rem" }} />
              <col style={{ width: "10.5rem" }} />
              {showBrandFitColumn ? <col style={{ width: "6rem" }} /> : null}
              <col style={{ width: "7rem" }} />
            </colgroup>
            <thead>
              <tr className="bg-theme-secondary">
                <SortableTh
                  {...sortThProps}
                  label=""
                  tooltip="Sort by rank"
                  sortKey="rank"
                  align="right"
                  className="!px-1 w-0"
                />
                <SortableTh {...sortThProps} label="Pattern" sortKey="pattern" />
                <SortableTh {...sortThProps} label="Type" sortKey="type" />
                <SortableTh {...sortThProps} label="Reach" sortKey="reach" align="right" />
                <SortableTh
                  {...sortThProps}
                  label="Creator impact"
                  tooltip="Performance vs each creator's typical video"
                  sortKey="creator_impact"
                  align="right"
                />
                {showBrandFitColumn ? (
                  <SortableTh {...sortThProps} label="Brand fit" sortKey="brand_fit" align="center" />
                ) : null}
                <th className="px-4 py-2" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-theme-secondary bg-theme-main">
              {sortedItems.map((item, index) => {
                const type = patternTypes.find((candidate) => candidate.id === item.pattern_type);
                const typeLabel = type?.label_long || type?.label || item.pattern_type;
                const typeDescription = PATTERN_TYPE_DESCRIPTIONS[item.pattern_type];
                const detailQuery = item.analysis_id
                  ? `?analysis_id=${encodeURIComponent(item.analysis_id)}`
                  : "";
                const patternPath = `/audience/${audience.hash}/highlights/${encodeURIComponent(item.pattern_type)}/${encodeURIComponent(item.pattern_key)}/overview/${detailQuery}`;
                const createPath = `/audience/${audience.hash}/highlights/${encodeURIComponent(item.pattern_type)}/${encodeURIComponent(item.pattern_key)}/scripts/${detailQuery}`;
                const reachRatio = item.baseline_comparison?.views;
                const creatorRatio = item.creator_comparison?.composite_outlier;
                const reachLabel = getReachLabel(reachRatio);
                const creatorLabel = getCreatorImpactLabel(creatorRatio);
                const analysisSummary = item.analysis?.description?.trim() || null;
                const analysisTooltip = formatAnalysisTooltip(item.analysis);

                return (
                  <tr
                    key={`${item.analysis_id}-${item.pattern_key}`}
                    onClick={() => navigate(patternPath)}
                    className="cursor-pointer hover:bg-theme-secondary transition-colors"
                  >
                    <td className="w-0 whitespace-nowrap px-1 py-3 text-right tabular-nums text-xs font-bold text-theme-text opacity-50">
                      #{item.rank ?? index + 1}
                    </td>
                    <td className="max-w-0 overflow-hidden px-4 py-3">
                      <div className="min-w-0">
                        <span className="inline-flex max-w-full min-w-0 items-center gap-1">
                          <span className="truncate font-semibold text-theme-text min-w-0" title={item.name}>
                            {item.name}
                          </span>
                          {analysisTooltip ? (
                            <span
                              className="shrink-0 cursor-help text-theme-text opacity-40 hover:opacity-70"
                              data-tooltip-id="highlight-analysis-tip"
                              data-tooltip-content={analysisTooltip}
                              aria-label="View pattern analysis"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <HeroIcon name="information-circle" className="h-4 w-4" />
                            </span>
                          ) : null}
                        </span>
                        {analysisSummary ? (
                          <div
                            className="mt-0.5 truncate text-xs text-theme-text opacity-50"
                            title={analysisSummary}
                          >
                            {analysisSummary}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center gap-2">
                        <HeroIcon name={type?.icon || "squares-2x2"} className="h-4 w-4 shrink-0 opacity-40" />
                        <div className="min-w-0">
                          <div className="text-sm text-theme-text">{typeLabel}</div>
                          {typeDescription?.subtitle ? (
                            <div className="text-xs text-theme-text opacity-50">{typeDescription.subtitle}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {reachLabel ? (
                        <div className={`text-sm font-semibold ${LABEL_TINT_CLASSES[reachLabel.tint] || "text-theme-text"}`}>
                          {reachLabel.emoji} {reachLabel.label}
                        </div>
                      ) : (
                        <div className="text-sm font-semibold text-theme-text opacity-40">—</div>
                      )}
                      <div className="mt-0.5 text-xs text-theme-text opacity-60">
                        {item.stats?.views != null ? `${formatNumber(item.stats.views)} views` : "—"}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {creatorLabel ? (
                        <div className={`text-sm font-semibold ${LABEL_TINT_CLASSES[creatorLabel.tint] || "text-theme-text"}`}>
                          {creatorLabel.emoji} {creatorLabel.label}
                        </div>
                      ) : (
                        <div className="text-sm font-semibold text-theme-text opacity-40">—</div>
                      )}
                      <div className="mt-0.5 text-xs text-theme-text opacity-60">
                        {formatRatioCompact(creatorRatio)?.text
                          ? `${formatRatioCompact(creatorRatio).text} creator avg`
                          : "—"}
                      </div>
                    </td>
                    {showBrandFitColumn ? (
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          {item.score_brand_fit != null ? (
                            <span title={item.brand_fit_reasoning || undefined}>
                              <FitScoreRadial score={item.score_brand_fit} />
                            </span>
                          ) : (
                            <span className="text-sm text-theme-text opacity-40">—</span>
                          )}
                        </div>
                      </td>
                    ) : null}
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={createPath}
                        onClick={(event) => event.stopPropagation()}
                        className="inline-block rounded-lg border border-theme-secondary bg-theme-secondary px-3 py-1 text-sm font-semibold text-theme-text transition-colors hover:border-theme-primary"
                      >
                        Create
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {sortedItems.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-theme-text opacity-60">
            No top patterns match this filter.
          </div>
        )}
      </div>

      <Tooltip
        id="highlight-analysis-tip"
        place="top"
        className="!max-w-sm !whitespace-pre-line !text-xs !leading-relaxed"
      />

      <ModalRoute
        path="/audience/:audienceHash/highlights/:patternType/:patternId/*"
        parentPath={`/audience/${audience?.hash}/highlights/`}
        noPadding
        element={
          <PatternDetail
            baseUrl={patternDetailBaseUrl}
            onClose={patternDetailOnClose}
            formatNumber={formatNumber}
            audience={audience}
            patternTypeLabel={selectedTypeMeta?.label_long ?? selectedTypeMeta?.label}
            patternType={routePatternType}
            fetchParams={
              audience?.hash && routePatternId
                ? {
                    audience_hash: audience.hash,
                    pattern_type: routePatternType,
                    pattern_key: routePatternId,
                    analysis_id: analysisIdParam || highlightItem?.analysis_id || undefined,
                  }
                : null
            }
          />
        }
        maxWidth="max-w-5xl"
        heightClass="h-[calc(90vh+60px)] max-h-[100vh]"
      />
    </div>
  );
};

function sortHighlightItems(items, sortBy, sortDir, patternTypes = []) {
  const dir = sortDir === "asc" ? 1 : -1;
  const copy = [...items];

  const typeLabel = (item) => {
    const type = patternTypes.find((candidate) => candidate.id === item.pattern_type);
    return (type?.label_long || type?.label || item.pattern_type || "").toLowerCase();
  };

  copy.sort((a, b) => {
    if (sortBy === "pattern") {
      const va = (a.name || "").toLowerCase();
      const vb = (b.name || "").toLowerCase();
      return dir * va.localeCompare(vb);
    }
    if (sortBy === "type") {
      return dir * typeLabel(a).localeCompare(typeLabel(b));
    }
    if (sortBy === "rank") {
      const va = a.rank ?? EMPTY_SENTINEL;
      const vb = b.rank ?? EMPTY_SENTINEL;
      if (va === vb) return 0;
      if (va === EMPTY_SENTINEL) return 1;
      if (vb === EMPTY_SENTINEL) return -1;
      return sortDir === "asc" ? vb - va : va - vb;
    }

    let va;
    let vb;
    switch (sortBy) {
      case "brand_fit":
        va = a.score_brand_fit ?? -1;
        vb = b.score_brand_fit ?? -1;
        break;
      case "reach":
        va = a.stats?.views ?? EMPTY_SENTINEL;
        vb = b.stats?.views ?? EMPTY_SENTINEL;
        break;
      case "creator_impact":
        va = a.creator_comparison?.composite_outlier ?? -1;
        vb = b.creator_comparison?.composite_outlier ?? -1;
        break;
      default:
        return 0;
    }

    if (va === vb) return 0;
    if (va === EMPTY_SENTINEL) return 1;
    if (vb === EMPTY_SENTINEL) return -1;
    return va < vb ? -dir : dir;
  });
  return copy;
}

export default Highlights;
